import {
  AppstoreOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CrownOutlined,
  DollarOutlined,
  ExclamationCircleFilled,
  GiftOutlined,
  GlobalOutlined,
  LoadingOutlined,
  PictureOutlined,
  ReloadOutlined,
  RiseOutlined,
  RobotOutlined,
  SmileOutlined,
  StarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  WarningFilled,
  WarningOutlined,
} from '@ant-design/icons'
import { Alert, Button, Collapse, Drawer, Result, Skeleton, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getVirtualIpDraft,
  getVirtualIpTypeRecommendation,
  regenerateVirtualIpTypeRecommendation,
  selectVirtualIpType,
  startVirtualIpTypeRecommendation,
  type SaasApiError,
  type VirtualIpDraft,
  type VirtualIpTypeCandidate,
  type VirtualIpTypeRecommendation,
} from '../../api'
import VirtualIpStepper from './VirtualIpStepper'
import { virtualIpCompletedThrough } from './virtualIpProgress'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import { confirmUpstreamEditIfNeeded } from './virtualIpUpstreamEdit'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import './virtualIp.css'

/** VISTOERA_VIRTUAL_IP_STEP2_IP_TYPE_SELECTION_UI_P0 (+
 * COMPARISON_UI_FIX_V1) — STEP 2 "IP 类型{t('common.select')}". Renders the real,
 * unmodified ``ip_type_recommendation_v1`` structured result the
 * backend already validated — every candidate field below is read
 * directly from `recommendation.candidates`, never reconstructed/
 * invented on the frontend, and every array access is defensively
 * guarded so a null/undefined/malformed backend payload degrades to
 * an empty state instead of crashing the page.
 *
 * AI recommendation ≠ final user decision: `selected` is a distinct
 * field from `candidates[0]`, always resolved from the backend, and
 * always restorable after a refresh.
 *
 * COMPARISON_UI_FIX_V1: every candidate card is fully expanded by
 * default (no t('common.viewDetails') collapse — STEP 2's job is side-by-side
 * comparison, not progressive disclosure), the primary "下一步" CTA
 * lives in the sticky right sidebar next to t('step2.selectedTypeTitle') instead
 * of a bottom-of-page footer, and "{t('common.backPreviousArrow')}" moved to the top header
 * next to t('common.backToOverview'). Selection state/logic is untouched.
 *
 * INFORMATION_HIERARCHY_UI_V2: presentation-only pass — no AI field is
 * renamed, reworded, merged, split, truncated, or invented. Every
 * `TypeCard` groups the *same* real fields into a 5-level visual
 * hierarchy (name+score header → one-sentence positioning → scannable
 * "quick decision" sections [strengths/platform/risks/best_for] →
 * lower-emphasis "deep analysis" sections [audience/monetization/
 * scalability/why-it-fits/visual preview] → the presentation-only rank
 * badge "01"/"02"/… which is UI metadata, never an AI-authored label
 * like "最佳"). `SectionBlock` is the one reusable heading+icon+body
 * primitive used for every field so headings are visually distinct
 * from body copy without introducing new content.
 */

type PageState = 'loading' | 'error' | 'needs_step1' | 'ready'

const POLL_INTERVAL_MS = 3000
const PRIMARY_CARD_COUNT = 4

const IP_TYPE_VISUALS: Record<string, { icon: ReactNode; className: string }> = {
  SELF_CLONE: { icon: <UserOutlined />, className: 'is-tone-a' },
  ENHANCED_SELF: { icon: <ThunderboltOutlined />, className: 'is-tone-b' },
  VIRTUAL_HUMAN: { icon: <RobotOutlined />, className: 'is-tone-c' },
  SEMI_REALISTIC_CHARACTER: { icon: <SmileOutlined />, className: 'is-tone-d' },
  CARTOON_HUMAN: { icon: <SmileOutlined />, className: 'is-tone-b' },
  ANTHROPOMORPHIC_ANIMAL: { icon: <GiftOutlined />, className: 'is-tone-c' },
  REALISTIC_ANIMAL: { icon: <GiftOutlined />, className: 'is-tone-a' },
  '3D_CHARACTER': { icon: <AppstoreOutlined />, className: 'is-tone-d' },
  FANTASY_CHARACTER: { icon: <CrownOutlined />, className: 'is-tone-b' },
  OBJECT_CHARACTER: { icon: <GiftOutlined />, className: 'is-tone-a' },
}

function typeVisual(ipType: unknown) {
  const key = typeof ipType === 'string' ? ipType : ''
  return IP_TYPE_VISUALS[key] || { icon: <PictureOutlined />, className: 'is-tone-a' }
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

/** Reusable Level-3/4 primitive: an icon + title heading (visually
 * distinct from body copy) followed by the real field content
 * (paragraph or bullet list), unchanged from the backend. `tone`
 * only affects the heading/icon color — never the underlying text —
 * so t('step2.sectionRisks') reads as a distinct, low-saturation warning without
 * a wall of red. */
function SectionBlock({
  icon,
  title,
  tone = 'default',
  children,
}: {
  icon: ReactNode
  title: string
  tone?: 'default' | 'warning' | 'muted'
  children: ReactNode
}) {
  return (
    <div className={`tc-vip-section tc-vip-section-${tone}`}>
      <div className="tc-vip-section-title">
        <span className="tc-vip-section-icon">{icon}</span>
        {title}
      </div>
      <div className="tc-vip-section-body">{children}</div>
    </div>
  )
}

function BulletList({ items, tone = 'default' }: { items: string[]; tone?: 'default' | 'warning' }) {
  if (items.length === 0) return null
  return (
    <ul className={`tc-vip-bullet-list${tone === 'warning' ? ' is-warning' : ''}`}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

export default function VirtualIpStep2Page() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()

  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const progress = useResolvedVirtualIpProgress()
  const [rec, setRec] = useState<VirtualIpTypeRecommendation | null>(null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [selectingType, setSelectingType] = useState<string | null>(null)
  const [showMore, setShowMore] = useState(false)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchRec = useCallback(
    (draftId: string) => {
      return getVirtualIpTypeRecommendation(workspaceId, draftId).then((r) => {
        setRec(r.data)
        return r.data
      })
    },
    [workspaceId],
  )

  const schedulePoll = useCallback(
    (draftId: string) => {
      if (pollTimer.current) clearTimeout(pollTimer.current)
      pollTimer.current = setTimeout(() => {
        fetchRec(draftId)
          .then((data) => {
            if (data.status === 'QUEUED' || data.status === 'RUNNING') {
              schedulePoll(draftId)
            }
          })
          .catch(() => schedulePoll(draftId))
      }, POLL_INTERVAL_MS)
    },
    [fetchRec],
  )

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    setPageState('loading')
    setLoadError(null)
    getVirtualIpDraft(workspaceId, projectId)
      .then((dRes) => {
        if (cancelled) return
        setDraft(dRes.data)
        if (virtualIpCompletedThrough(dRes.data.status) < 1) {
          setPageState('needs_step1')
          return
        }
        return fetchRec(dRes.data.id).then((data) => {
          if (cancelled) return
          setPageState('ready')
          if (data.status === 'QUEUED' || data.status === 'RUNNING') {
            schedulePoll(dRes.data.id)
          }
        })
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError((e as SaasApiError).message || t('common.loadFailedRefresh'))
        setPageState('error')
      })
    return () => {
      cancelled = true
      if (pollTimer.current) clearTimeout(pollTimer.current)
    }
  }, [workspaceId, fetchRec, schedulePoll])

  const handleStart = () => {
    if (!draft) return
    setActionLoading(true)
    startVirtualIpTypeRecommendation(workspaceId, draft.id)
      .then((r) => {
        setRec(r.data)
        setActionLoading(false)
        setShowMore(false)
        if (r.data.status === 'QUEUED' || r.data.status === 'RUNNING') {
          schedulePoll(draft.id)
        }
      })
      .catch((e) => {
        setActionLoading(false)
        message.error((e as SaasApiError).message || t('step2.startAnalysisFailed'))
      })
  }

  const handleRegenerate = () => {
    if (!draft) return
    setActionLoading(true)
    regenerateVirtualIpTypeRecommendation(workspaceId, draft.id)
      .then((r) => {
        setRec(r.data)
        setActionLoading(false)
        setShowMore(false)
        if (r.data.status === 'QUEUED' || r.data.status === 'RUNNING') {
          schedulePoll(draft.id)
        }
      })
      .catch((e) => {
        setActionLoading(false)
        message.error((e as SaasApiError).message || t('step2.retryAnalysisFailed'))
      })
  }

  const handleSelect = (candidate: VirtualIpTypeCandidate) => {
    if (!draft || !rec?.run_id) return
    const ipType = candidate.ip_type
    if (!ipType || typeof ipType !== 'string') return
    const runSelect = () => {
      setSelectingType(ipType)
      return selectVirtualIpType(workspaceId, draft.id, ipType, rec.run_id!)

        .then((r) => {
          setRec(r.data)
          setSelectingType(null)
        })
        .catch((e) => {
          setSelectingType(null)
          message.error((e as SaasApiError).message || t('common.selectFailed'))
        })
    }
    const hasDownstream =
      draft.status === 'step_3_completed' || draft.status === 'positioning_confirmed'
    confirmUpstreamEditIfNeeded({
      fromStep: 2,
      hasDownstreamArtifacts: hasDownstream,
      onConfirm: runSelect,
    })
  }

  if (pageState === 'loading') {
    return (
      <div className="tc-vip-page">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 20 }} />
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div className="tc-vip-page">
        <Alert type="error" showIcon message={t('common.loadFailed')} description={loadError} />
      </div>
    )
  }

  if (pageState === 'needs_step1') {
    return (
      <div className="tc-vip-page" data-testid="virtual-ip-step2-needs-step1">
        <PageHeader />
        <VirtualIpStepper currentStep={2} progress={progress} />
        <Result
          status="info"
          title={t('step2.prerequisiteTitle')} subTitle={t('step2.prerequisiteSubtitle')}
          extra={
            <Button type="primary" onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-1`)}>
              {t('common.backToStep', { step: 1 })}
            </Button>
          }
        />
      </div>
    )
  }

  const status = rec?.status ?? 'NOT_STARTED'
  const candidates = Array.isArray(rec?.candidates) ? (rec!.candidates as VirtualIpTypeCandidate[]) : null
  const sortedCandidates = candidates
    ? [...candidates].sort((a, b) => (typeof a?.rank === 'number' ? a.rank : 99) - (typeof b?.rank === 'number' ? b.rank : 99))
    : null
  const selected = rec?.selected ?? null
  const hasSelection = Boolean(selected?.ip_type)
  const isBusyStatus = status === 'QUEUED' || status === 'RUNNING'
  const nextDisabled = !hasSelection || status === 'FAILED' || isBusyStatus

  let nextDisabledReason = ''
  if (status === 'FAILED') nextDisabledReason = t('step2.analysisFailedRetry')
  else if (isBusyStatus) nextDisabledReason = t('step2.analyzingWait')
  else if (!hasSelection) nextDisabledReason = t('step2.selectTypeRequired')

  return (
    <div className="tc-vip-page" data-testid="virtual-ip-step2-page">
      <PageHeader onBack={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-1`)} />
      <VirtualIpStepper currentStep={2} progress={progress} />

      <div className="tc-vip-layout">
        <div className="tc-vip-main">
          <CandidateArea
            status={status}
            candidates={sortedCandidates}
            selected={selected}
            showMore={showMore}
            setShowMore={setShowMore}
            selectingType={selectingType}
            onSelect={handleSelect}
            onRegenerate={handleRegenerate}
            actionLoading={actionLoading}
            errorMessage={rec?.error_message ?? null}
            errorCode={rec?.error_code ?? null}
          />
        </div>

        <aside className="tc-vip-aside">
          <AiPreviewPanel
            status={status}
            rec={rec}
            selected={selected}
            actionLoading={actionLoading}
            onStart={handleStart}
            onRegenerate={handleRegenerate}
            onViewFull={() => setDetailDrawerOpen(true)}
            nextDisabled={nextDisabled}
            nextDisabledReason={nextDisabledReason}
            onNext={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-3`)}
          />
        </aside>
      </div>

      <Drawer title={t('step2.drawerTitle')} open={detailDrawerOpen} onClose={() => setDetailDrawerOpen(false)} width={420} className="tc-vip-drawer">
        <FullAnalysisDrawerBody rec={rec} />
      </Drawer>
    </div>
  )
}

function PageHeader({ onBack }: { onBack?: () => void }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const navigate = useNavigate()
  const { workspaceId = '' } = useParams()
  return (
    <div className="tc-page-header">
      <div>
        <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
        <div className="tc-vip-step-label">
          <span>STEP 2 / {VIRTUAL_IP_FLOW_TOTAL_STEPS}</span>
          <span className="tc-vip-step-title">{t('step2.title')}</span>
        </div>
        <p className="tc-page-desc">{t('step2.description')}</p>
      </div>
      <div className="tc-vip-header-actions">
        {onBack && (
          <Button size="small" onClick={onBack} data-testid="vip-step2-back-button">
            {t('common.backPreviousArrow')}
          </Button>
        )}
        <Button size="small" onClick={() => navigate(`/workspace/${workspaceId}/overview`)}>
          {t('common.backToOverview')}
        </Button>
      </div>
    </div>
  )
}

function CandidateArea({
  status,
  candidates,
  selected,
  showMore,
  setShowMore,
  selectingType,
  onSelect,
  onRegenerate,
  actionLoading,
  errorMessage,
  errorCode,
}: {
  status: string
  candidates: VirtualIpTypeCandidate[] | null
  selected: VirtualIpTypeRecommendation['selected']
  showMore: boolean
  setShowMore: (v: boolean) => void
  selectingType: string | null
  onSelect: (c: VirtualIpTypeCandidate) => void
  onRegenerate: () => void
  actionLoading: boolean
  errorMessage: string | null
  errorCode: string | null
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-candidate-area">
      <div className="tc-vip-card-title">{t('step2.resultsTitle')}</div>
      <p className="tc-vip-card-desc">{t('step2.resultsDesc')}</p>

      {status === 'NOT_STARTED' && (
        <div className="tc-vip-empty-state" data-testid="vip-type-not-started">
          <PictureOutlined className="tc-vip-empty-icon" />
          <p>{t('step2.noAnalysisResults')}</p>
          <span className="tc-vip-card-hint">{t('step2.startAnalysisHint')}</span>
        </div>
      )}

      {(status === 'QUEUED' || status === 'RUNNING') && (
        <div className="tc-vip-card-grid" data-testid="vip-type-running">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="tc-vip-type-card is-loading">
              <Skeleton active avatar={{ shape: 'square', size: 64 }} paragraph={{ rows: 3 }} />
            </div>
          ))}
        </div>
      )}

      {status === 'FAILED' && (
        <div className="tc-vip-fail-card" data-testid="vip-type-failed">
          <ExclamationCircleFilled className="tc-vip-fail-icon" />
          <div className="tc-vip-fail-title">{t('step2.failTitle')}</div>
          <p className="tc-vip-fail-desc">{t('step2.failDesc')}</p>
          <Button danger loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
            {t('step2.retryAnalysis')}
          </Button>
          {(errorMessage || errorCode) && (
            <Collapse
              ghost
              className="tc-vip-fail-tech"
              items={[
                {
                  key: 'tech',
                  label: t('common.viewTechDetails'),
                  children: (
                    <code className="tc-vip-fail-tech-text">
                      {errorCode ? `[${errorCode}] ` : ''}
                      {errorMessage || '(no error message)'}
                    </code>
                  ),
                },
              ]}
            />
          )}
        </div>
      )}

      {status === 'STALE' && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          message={t('step2.staleTitle')} description={t('step2.staleDesc')} action={
            <Button size="small" loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
              {t('step2.reanalyzeWithLatest')}
            </Button>
          }
          data-testid="vip-type-stale-banner"
          style={{ marginBottom: 12 }}
        />
      )}

      {(status === 'COMPLETED' || status === 'STALE') &&
        (!candidates || candidates.length === 0 ? (
          <div className="tc-vip-empty-state" data-testid="vip-type-empty-result">
            <PictureOutlined className="tc-vip-empty-icon" />
            <p>{t('step2.noCandidatesReturned')}</p>
            <Button onClick={onRegenerate} icon={<ReloadOutlined />}>
              {t('step2.retryAnalysis')}
            </Button>
          </div>
        ) : (
          <CandidateGrid
            candidates={candidates}
            selected={selected}
            showMore={showMore}
            setShowMore={setShowMore}
            selectingType={selectingType}
            onSelect={onSelect}
          />
        ))}
    </div>
  )
}

function CandidateGrid({
  candidates,
  selected,
  showMore,
  setShowMore,
  selectingType,
  onSelect,
}: {
  candidates: VirtualIpTypeCandidate[]
  selected: VirtualIpTypeRecommendation['selected']
  showMore: boolean
  setShowMore: (v: boolean) => void
  selectingType: string | null
  onSelect: (c: VirtualIpTypeCandidate) => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const primary = candidates.slice(0, PRIMARY_CARD_COUNT)
  const extra = candidates.slice(PRIMARY_CARD_COUNT)
  const visible = showMore ? candidates : primary

  return (
    <>
      <div className="tc-vip-card-grid" data-testid="vip-type-candidate-grid">
        {visible.map((candidate, idx) => (
          <TypeCard
            key={typeof candidate?.ip_type === 'string' ? candidate.ip_type : idx}
            candidate={candidate}
            rank={idx + 1}
            isSelected={Boolean(selected?.ip_type) && selected?.ip_type === candidate?.ip_type}
            isSelecting={selectingType === candidate?.ip_type}
            onSelect={() => onSelect(candidate)}
          />
        ))}
      </div>
      {extra.length > 0 && (
        <div className="tc-vip-show-more">
          <Button type="link" onClick={() => setShowMore(!showMore)}>
            {showMore ? t('common.collapse') : t('step2.showMoreTypes', { count: candidates.length })}
          </Button>
        </div>
      )}
    </>
  )
}

function TypeCard({
  candidate,
  rank,
  isSelected,
  isSelecting,
  onSelect,
}: {
  candidate: VirtualIpTypeCandidate
  rank: number
  isSelected: boolean
  isSelecting: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const displayName =
    typeof candidate?.display_name === 'string' ? candidate.display_name : String(candidate?.ip_type ?? t('common.unnamedType'))
  const score = typeof candidate?.suitability_score === 'number' ? candidate.suitability_score : null
  const positioning = typeof candidate?.one_sentence_positioning === 'string' ? candidate.one_sentence_positioning : ''
  const visual = typeVisual(candidate?.ip_type)
  const rankLabel = String(rank).padStart(2, '0')

  return (
    <div
      className={`tc-vip-type-card${isSelected ? ' is-selected' : ''}`}
      data-testid="vip-type-card"
      data-ip-type={typeof candidate?.ip_type === 'string' ? candidate.ip_type : undefined}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect()
      }}
    >
      {isSelected && (
        <div className="tc-vip-type-card-check">
          <CheckOutlined />
        </div>
      )}

      {/* LEVEL 1 — strongest text on the card: name + fit score. The
       * "01" badge is presentation-only ranking metadata (not an AI
       * conclusion like "最佳") so users can talk about "方案 01/02/03/04". */}
      <div className="tc-vip-type-header">
        <div className="tc-vip-type-visual">
          <div className={`tc-vip-type-thumb ${visual.className}`}>{visual.icon}</div>
          <span className="tc-vip-type-rank" data-testid="vip-type-rank" aria-hidden="true">
            {rankLabel}
          </span>
        </div>
        <div className="tc-vip-type-heading">
          <span className="tc-vip-type-name">{displayName}</span>
          {score !== null && (
            <div className="tc-vip-type-score">
              <span className="tc-vip-type-score-value">{score}%</span>
              <span className="tc-vip-type-score-label">{t('step2.fitScoreLabel')}</span>
            </div>
          )}
        </div>
      </div>

      {/* LEVEL 2 — core positioning description, brighter/larger than
       * body copy but still full, unclamped text. */}
      {positioning && <p className="tc-vip-type-positioning">{positioning}</p>}

      <TypeCardQuickDecision candidate={candidate} />
      <TypeCardDeepAnalysis candidate={candidate} />

      {isSelecting && (
        <div className="tc-vip-type-card-overlay">
          <LoadingOutlined spin />
        </div>
      )}
    </div>
  )
}

/** LEVEL 3 — "at a glance" decision fields, each its own labeled
 * section so a reader scanning across all 4 cards can jump straight
 * to t('step2.sectionRisks') or t('step2.sectionBestAudience') without re-reading a full paragraph.
 * Every field is the same real backend value as before — only grouped
 * under a heading instead of sitting in one long undifferentiated block. */
function TypeCardQuickDecision({ candidate }: { candidate: VirtualIpTypeCandidate }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const strengths = asStringArray(candidate?.strengths)
  const platformFit = typeof candidate?.platform_fit === 'string' ? candidate.platform_fit : ''
  const risks = asStringArray(candidate?.risks)
  const bestFor = asStringArray(candidate?.best_for)

  if (strengths.length === 0 && !platformFit && risks.length === 0 && bestFor.length === 0) return null

  return (
    <div className="tc-vip-type-quick" onClick={(e) => e.stopPropagation()}>
      {strengths.length > 0 && (
        <SectionBlock icon={<StarOutlined />} title={t('step2.sectionCoreStrengths')}>
          <BulletList items={strengths} />
        </SectionBlock>
      )}
      {platformFit && (
        <SectionBlock icon={<GlobalOutlined />} title={t('step2.sectionPlatforms')}>
          <p>{platformFit}</p>
        </SectionBlock>
      )}
      {risks.length > 0 && (
        <SectionBlock icon={<WarningOutlined />} title={t('step2.sectionRisks')} tone="warning">
          <BulletList items={risks} tone="warning" />
        </SectionBlock>
      )}
      {bestFor.length > 0 && (
        <SectionBlock icon={<TeamOutlined />} title={t('step2.sectionBestAudience')}>
          <BulletList items={bestFor} />
        </SectionBlock>
      )}
    </div>
  )
}

/** LEVEL 4 — the full AI analysis text, unabridged. Still fully
 * visible (no accordion/clamp per COMPARISON_UI_FIX_V1), just a
 * visually quieter tier than LEVEL 3 so the two most decision-critical
 * groups (name/score/positioning + quick-decision sections) win the
 * first pass and this detail is there for whoever wants to read it. */
function TypeCardDeepAnalysis({ candidate }: { candidate: VirtualIpTypeCandidate }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const why = asStringArray(candidate?.why_it_fits)
  const visualPreview = asStringArray(candidate?.visual_direction_preview)
  const audienceFit = typeof candidate?.audience_fit === 'string' ? candidate.audience_fit : ''
  const monetizationFit = typeof candidate?.monetization_fit === 'string' ? candidate.monetization_fit : ''
  const scalability = typeof candidate?.long_term_scalability === 'string' ? candidate.long_term_scalability : ''

  if (!audienceFit && !monetizationFit && !scalability && why.length === 0 && visualPreview.length === 0) return null

  return (
    <div className="tc-vip-type-deep" onClick={(e) => e.stopPropagation()}>
      {audienceFit && (
        <SectionBlock icon={<UsergroupAddOutlined />} title={t('step2.sectionAudienceFit')} tone="muted">
          <p>{audienceFit}</p>
        </SectionBlock>
      )}
      {monetizationFit && (
        <SectionBlock icon={<DollarOutlined />} title={t('step2.sectionMonetizationFit')} tone="muted">
          <p>{monetizationFit}</p>
        </SectionBlock>
      )}
      {scalability && (
        <SectionBlock icon={<RiseOutlined />} title={t('step2.sectionScalability')} tone="muted">
          <p>{scalability}</p>
        </SectionBlock>
      )}
      {why.length > 0 && (
        <SectionBlock icon={<BulbOutlined />} title={t('step2.sectionWhyFit')} tone="muted">
          <BulletList items={why} />
        </SectionBlock>
      )}
      {visualPreview.length > 0 && (
        <SectionBlock icon={<PictureOutlined />} title={t('step2.sectionVisualPreview')} tone="muted">
          <div className="tc-vip-options">
            {visualPreview.map((v, i) => (
              <Tag key={i}>{v}</Tag>
            ))}
          </div>
        </SectionBlock>
      )}
    </div>
  )
}

function statusBadge(status: string, t: (key: string) => string) {
  switch (status) {
    case 'NOT_STARTED':
      return { text: t('common.pendingGenerate'), className: 'is-neutral' }
    case 'QUEUED':
    case 'RUNNING':
      return { text: t('jobStatus.analyzing'), className: 'is-info' }
    case 'COMPLETED':
      return { text: t('jobStatus.completed'), className: 'is-success' }
    case 'FAILED':
      return { text: t('jobStatus.analyzeFailed'), className: 'is-error' }
    case 'STALE':
      return { text: t('jobStatus.needsReanalyze'), className: 'is-warning' }
    default:
      return { text: t('common.pendingGenerate'), className: 'is-neutral' }
  }
}

function AiPreviewPanel({
  status,
  rec,
  selected,
  actionLoading,
  onStart,
  onRegenerate,
  onViewFull,
  nextDisabled,
  nextDisabledReason,
  onNext,
}: {
  status: string
  rec: VirtualIpTypeRecommendation | null
  selected: VirtualIpTypeRecommendation['selected']
  actionLoading: boolean
  onStart: () => void
  onRegenerate: () => void
  onViewFull: () => void
  nextDisabled: boolean
  nextDisabledReason: string
  onNext: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const progressStages = t('step2.progressStages', { returnObjects: true }) as string[]
  const analysisIncludes = t('step2.analysisIncludesList', { returnObjects: true }) as string[]
  const badge = statusBadge(status, t)
  const selectedSnapshot = selected?.snapshot ?? null
  const selectedDisplayName =
    (selectedSnapshot && typeof selectedSnapshot.display_name === 'string' && selectedSnapshot.display_name) ||
    selected?.ip_type ||
    null
  const selectedScore =
    selectedSnapshot && typeof selectedSnapshot.suitability_score === 'number' ? selectedSnapshot.suitability_score : null
  // t('step2.selectionReasonTitle') only renders when the backend actually returned
  // `why_it_fits` for the selected candidate — never fabricated to
  // match a mockup; if the field is absent the whole block is omitted.
  const selectionReasons = selectedSnapshot ? asStringArray(selectedSnapshot.why_it_fits) : []

  return (
    <div className="tc-data-card tc-vip-preview-panel" data-testid="vip-ai-preview-panel">
      <div className="tc-vip-preview-panel-head">
        <span className="tc-vip-card-title">{t('step2.previewTitle')}</span>
        <span className={`tc-vip-status-badge ${badge.className}`}>{badge.text}</span>
      </div>

      {status === 'NOT_STARTED' && (
        <div className="tc-vip-preview-body">
          <p className="tc-vip-card-desc">{t('step2.startAnalysisDesc')}</p>
          <p className="tc-vip-card-hint">{t('step2.startAnalysisHint2')}</p>
          <Button type="primary" block loading={actionLoading} onClick={onStart}>
            {t('step2.startAnalysis')}
          </Button>
        </div>
      )}

      {(status === 'QUEUED' || status === 'RUNNING') && (
        <div className="tc-vip-preview-body">
          <p className="tc-vip-card-desc">
            <LoadingOutlined spin style={{ marginRight: 6 }} />
            {t('step2.analyzingInProgress')}
          </p>
          <ul className="tc-vip-stage-list">
            {progressStages.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      )}

      {status === 'COMPLETED' && (
        <div className="tc-vip-preview-body">
          <p className="tc-vip-card-desc">{t('step2.analysisIncludes')}</p>
          <ul className="tc-vip-check-list">
            {analysisIncludes.map((label) => (
              <li key={label}>
                <CheckCircleFilled className="tc-vip-check-icon" /> {label}
              </li>
            ))}
          </ul>
          <Button block onClick={onViewFull}>
            {t('step2.viewFullAnalysis')}
          </Button>
        </div>
      )}

      {status === 'FAILED' && (
        <div className="tc-vip-preview-body">
          <p className="tc-vip-card-desc">{t('step2.failTitle')}</p>
          <p className="tc-vip-card-hint">{t('step2.failDesc')}</p>
          <Button danger block loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
            {t('step2.retryAnalysis')}
          </Button>
        </div>
      )}

      {status === 'STALE' && (
        <div className="tc-vip-preview-body">
          <p className="tc-vip-card-desc">{t('step2.staleTitle')}</p>
          <p className="tc-vip-card-hint">{t('step2.sidebarStaleHint')}</p>
          <Button block loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
            {t('step2.reanalyzeWithLatest')}
          </Button>
        </div>
      )}

      {selectedDisplayName && (
        <div className="tc-vip-selected-summary" data-testid="vip-selected-summary">
          <span className="tc-vip-section-title tc-vip-section-title-plain">{t('step2.selectedTypeTitle')}</span>
          <div className="tc-vip-selected-summary-value">
            <CheckCircleFilled className="tc-vip-check-icon" />
            {String(selectedDisplayName)}
            {selectedScore !== null && <span className="tc-vip-selected-summary-score">{t('common.fitScore', { score: selectedScore })}</span>}
          </div>
          {rec?.run_id && <div className="tc-vip-card-hint">Run ID: {rec.run_id.slice(0, 8)}…</div>}
        </div>
      )}

      {selectionReasons.length > 0 && (
        <div className="tc-vip-selection-reasons" data-testid="vip-selection-reasons">
          <span className="tc-vip-section-title tc-vip-section-title-plain">{t('step2.selectionReasonTitle')}</span>
          <ul className="tc-vip-check-list">
            {selectionReasons.map((reason, i) => (
              <li key={i}>
                <CheckCircleFilled className="tc-vip-check-icon" /> {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tc-vip-next-cta">
        {selectedDisplayName && <p className="tc-vip-next-cta-hint">{t('step2.nextCtaHint')}</p>}
        {nextDisabled ? (
          <Tooltip title={nextDisabledReason}>
            <span>
              <Button type="primary" block disabled data-testid="vip-step2-next-button">
                {t('step2.nextProposalsArrow')}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button type="primary" block onClick={onNext} data-testid="vip-step2-next-button">
            {t('step2.nextProposalsArrow')}
          </Button>
        )}
      </div>
    </div>
  )
}

function FullAnalysisDrawerBody({ rec }: { rec: VirtualIpTypeRecommendation | null }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const summary = rec?.analysis_summary
  const guidance = rec?.decision_guidance
  if (!summary && !guidance) {
    return <p className="tc-vip-card-hint">{t('step2.noAnalysisContent')}</p>
  }
  const keyConstraints = summary ? asStringArray((summary as Record<string, unknown>).key_constraints) : []
  return (
    <div className="tc-vip-drawer-body">
      {summary && (
        <div className="tc-vip-analysis-subblock">
          <div className="tc-vip-card-title" style={{ marginBottom: 8 }}>
            {t('step2.analysisBasis')}
          </div>
          <dl className="tc-vip-analysis-dl">
            <dt>{t('step2.primaryGoal')}</dt>
            <dd>{String((summary as Record<string, unknown>).primary_goal ?? '—')}</dd>
            <dt>{t('step2.commercialLogic')}</dt>
            <dd>{String((summary as Record<string, unknown>).commercial_logic ?? '—')}</dd>
            <dt>{t('step2.audienceLogic')}</dt>
            <dd>{String((summary as Record<string, unknown>).audience_logic ?? '—')}</dd>
            <dt>{t('step2.platformLogic')}</dt>
            <dd>{String((summary as Record<string, unknown>).platform_logic ?? '—')}</dd>
          </dl>
          {keyConstraints.length > 0 && (
            <div className="tc-vip-analysis-subblock">
              <span className="tc-vip-subfield-title">{t('step2.sectionKeyConstraints')}</span>
              <div className="tc-vip-options">
                {keyConstraints.map((c, i) => (
                  <Tag key={i}>{c}</Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {guidance && (
        <div className="tc-vip-analysis-subblock" style={{ marginTop: 16 }}>
          <div className="tc-vip-card-title" style={{ marginBottom: 8 }}>
            {t('step2.decisionGuidance')}
          </div>
          <p className="tc-vip-analysis-reasoning">
            <strong>{t('step2.prioritizeTrust')}</strong>
            {String((guidance as Record<string, unknown>).best_if_prioritize_trust ?? '—')}
          </p>
          <p className="tc-vip-analysis-reasoning">
            <strong>{t('step2.prioritizeDistinctiveness')}</strong>
            {String((guidance as Record<string, unknown>).best_if_prioritize_distinctiveness ?? '—')}
          </p>
          <p className="tc-vip-analysis-reasoning">
            <strong>{t('step2.prioritizeScalability')}</strong>
            {String((guidance as Record<string, unknown>).best_if_prioritize_scalability ?? '—')}
          </p>
          <p className="tc-vip-analysis-reasoning">
            <strong>{t('step2.prioritizeLowCost')}</strong>
            {String((guidance as Record<string, unknown>).best_if_prioritize_low_creation_cost ?? '—')}
          </p>
        </div>
      )}
    </div>
  )
}
